import React from 'react';
import * as api from '../services/api';

function formatMonthly(cents) {
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(2)}/mo`;
}

function formatAnnual(cents) {
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(2)}/yr`;
}

function calcAnnualSavingsPct(monthlyPriceCents, annualPriceCents) {
  if (!monthlyPriceCents || !annualPriceCents || monthlyPriceCents <= 0) return 0;
  const annualEquivMonthly = annualPriceCents / 12;
  return Math.round((1 - annualEquivMonthly / monthlyPriceCents) * 100);
}

// Feature rows matching the spec
const FEATURES = [
  { id: 'clip_search', label: 'Clip search' },
  { id: 'search_window', label: 'Search history window' },
  { id: 'advanced_filters', label: 'Advanced search filters' },
  { id: 'collections', label: 'Collection' },
  { id: 'collection_tagging', label: 'Collection tagging' },
  { id: 'favorite_channels', label: 'Favorite channels' },
  { id: 'playback', label: 'Clip Playback' },
  { id: 'export_basic', label: 'Export: basic' },
  { id: 'export_max_clips', label: 'Export: max clips' },
  { id: 'export_any', label: 'Export: any channel' },
];

function getFeatureValue(tier, featureId) {
  /* c8 ignore start */
  if (!tier || !tier.features) return '—';

  const features = tier.features;
  
  switch (featureId) {
    case 'clip_search':
      return 'Any channel';
    
    case 'search_window': {
      // Prefer new schema `search.maxDays`, fall back to other legacy keys.
      const raw = features.search?.maxDays ?? features.search?.windowDays ?? features.search_window ?? features.searchWindow ?? (tier.name === 'Free' ? 30 : 60);
      if (raw == null) return '—';
      const n = Number(raw);
      return Number.isFinite(n) ? `${n} days` : String(raw);
    }
    
    case 'advanced_filters':
      return features.search?.advancedFilters ? '✓' : '—';
    
    case 'collections':
      return features.collection?.maxClips ? `Up to ${features.collection.maxClips} clips` : '—';
    
    case 'collection_tagging':
      return features.collection?.tagging ? '✓' : '—';
    
    case 'favorite_channels':
      return features.favorites?.maxChannels ? `Up to ${features.favorites.maxChannels}` : '—';
    
    case 'playback':
      return features.playback?.enabled ? '✓' : '—';
    
    case 'export_basic':
      // Support nested and legacy keys for daily limit
      {
        const daily = features.export?.dailyLimit ?? features.daily_export_limit ?? features.dailyLimit ?? null;
        return daily != null ? `${daily}/day` : '—';
      }
    
    case 'export_max_clips': {
      // Support nested and legacy keys for max clips per export
      const raw = features.export?.maxClipsPerExport ?? features.export_max_clips ?? features.max_clips_per_export ?? features.maxClips ?? null;
      if (raw == null) return '—';
      const n = Number(raw);
      return Number.isFinite(n) ? `${n}` : String(raw);
    }
    
    case 'export_any':
      // Support nested and legacy keys for own-channel flag
      {
        const ownChannel = features.export?.ownChannelOnly ?? features.export_own_channel_only ?? features.ownChannelOnly ?? null;
        if (ownChannel === false) return 'Any channel';
        if (ownChannel === true) return tier.name === 'Free' ? 'Your channel only' : 'Own channel only';
        return tier.name === 'Free' ? 'Your channel only' : '—';
      }
    
    default:
      return '—';
  }
  /* c8 ignore stop */
}

/**
 * Render the CTA button for a tier/billing cycle combination.
 *
 * Rules:
 * - Free tier (price = 0): always "Get started" → /login if unauth, /dashboard if auth
 * - Unauthenticated paid tier: purple "Log in to subscribe" → /login, with "Test it out first — log in to subscribe" message
 * - Authenticated, current tier matches: "Current plan" badge (no action)
 * - Authenticated on waiting list (tier name contains "waiting"): "On waiting list" badge
 * - Authenticated, paid tier available: purple "Subscribe" / "Subscribe annually"
 * - No stripe price configured: disabled "Not available"
 */
function TierCTA({ tier, billing, user }) {
  const isPaid = tier.price_monthly_cents > 0;
  const priceId = billing === 'annual'
    ? (tier.stripe_price_id_annual || tier.stripe_price_id)
    : (tier.stripe_price_id_monthly || tier.stripe_price_id);

  const userTierName = user?.tier?.name;
  const isCurrentTier = userTierName && userTierName === tier.name;
  const isOnWaitingList = userTierName && userTierName.toLowerCase().includes('waiting');

  // Free tier
  if (!isPaid) {
    if (!user) {
      return (
        <a href="/login" className="inline-block px-3 py-2 bg-dark-700 hover:bg-dark-600 text-gray-300 rounded-sm text-sm transition-colors" aria-label={`Get started with ${tier.name}`}>
          Get started
        </a>
      );
    }
    if (isCurrentTier) {
      return <span className="inline-block px-3 py-1 bg-dark-700 text-gray-400 rounded-sm text-xs">Current plan</span>;
    }
    return (
      <a href="/dashboard" className="inline-block px-3 py-2 bg-dark-700 hover:bg-dark-600 text-gray-300 rounded-sm text-sm transition-colors" aria-label={`Go to ${tier.name} dashboard`}>
        Get started
      </a>
    );
  }

  // Paid tier — unauthenticated
  if (!user) {
    return (
      <a
        href="/login"
        className="inline-block px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-sm text-sm font-medium transition-colors"
        aria-label={`Log in to subscribe to ${tier.name}`}
      >
        Log in to subscribe
      </a>
    );
  }

  // Authenticated — current plan
  if (isCurrentTier) {
    return <span className="inline-block px-3 py-1 bg-purple-900/40 text-purple-300 border border-purple-700/50 rounded-sm text-xs font-medium">Current plan</span>;
  }

  // Authenticated — on waiting list (they can still subscribe)
  // Authenticated — no stripe price configured for this billing cycle
  if (!priceId) {
    return <button className="px-3 py-2 bg-dark-700 text-gray-500 rounded-sm text-sm" disabled title="Price not configured">Not available</button>;
  }

  // Authenticated — show subscribe button
  const label = billing === 'annual' ? `Subscribe annually to ${tier.name}` : `Subscribe to ${tier.name}`;
  const btnText = billing === 'annual' ? 'Subscribe annually' : 'Subscribe';

  return (
    <button
      className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-sm text-sm font-medium transition-colors"
      onClick={async () => {
        try {
          const session = await api.createCheckoutSession(priceId);
          if (session && session.url) {
            window.location.assign(session.url);
          } else {
            alert('Unable to create checkout session.');
          }
        } catch (err) {
          /* c8 ignore start */
          if (err && err.status === 401) {
            window.location.assign('/login');
          } else {
            alert(err.message || 'Unable to create checkout session');
          }
          /* c8 ignore stop */
        }
      }}
      aria-label={label}
    >
      {btnText}
    </button>
  );
}

export default function TierComparisonGrid({ tiers = [], loading = false, user = undefined }) {
  if (loading) {
    return (
      <div className="w-full py-8">
        <div className="max-w-3xl mx-auto bg-dark-800 rounded-lg border border-dark-700 p-6">
          <div className="animate-pulse bg-dark-700 rounded-sm h-64" />
        </div>
      </div>
    );
  }

  if (!tiers || tiers.length === 0) {
    return (
      <div className="w-full py-8 text-center">
        <p className="text-gray-400">No tiers available.</p>
      </div>
    );
  }

  // Unauthenticated hint message
  const showLoginHint = user === null;

  return (
    <div className="w-full">
      {showLoginHint && (
        <div className="max-w-3xl mx-auto mb-4 px-4 py-3 bg-dark-800 border border-dark-600 rounded-lg text-sm text-gray-400 text-center">
          <span className="text-gray-300 font-medium">Test it out first</span> — explore the features below, then log in to subscribe when you're ready.
        </div>
      )}
      <div className="max-w-3xl mx-auto bg-dark-800 rounded-lg border border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-auto text-left border-collapse">
            <thead>
              <tr className="bg-dark-700 border-b border-dark-700">
                <th className="py-4 px-4 text-sm font-semibold text-gray-300 text-left">Feature</th>
                {tiers.map((t) => (
                  <th key={t.id} className="py-4 px-4 text-sm font-semibold text-white text-center">
                    <div>{t.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-dark-700">
                <td className="py-4 px-4 text-sm text-gray-300">Description</td>
                {tiers.map((t) => (
                  <td key={`${t.id}-description`} className="py-4 px-4 text-sm text-gray-300 text-center">
                    {t.description ?? '—'}
                  </td>
                ))}
              </tr>

              {FEATURES.map((feature) => (
                <tr key={feature.id} className="border-b border-dark-700 hover:bg-dark-800/50 transition-colors">
                  <td className="py-4 px-4 text-sm text-gray-300">{feature.label}</td>
                  {tiers.map((t) => (
                    <td key={`${t.id}-${feature.id}`} className="py-4 px-4 text-sm text-gray-300 text-center">
                      {getFeatureValue(t, feature.id)}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Price row */}
              <tr className="border-t-2 border-dark-600 bg-dark-800/30">
                <td className="py-4 px-4 text-sm font-semibold text-white">Price</td>
                {tiers.map((t) => (
                  <td key={`${t.id}-price`} className="py-4 px-4 text-sm font-semibold text-white text-center">
                    <div>{formatMonthly(t.price_monthly_cents)}</div>
                    <div className="text-xs text-gray-400 mt-1">{formatAnnual(t.price_annual_cents ?? 0)}</div>
                  </td>
                ))}
              </tr>

              {/* Monthly CTA row */}
              <tr className="bg-dark-900 border-t border-dark-700">
                <td className="py-4 px-4 text-sm font-semibold text-white">Monthly</td>
                {tiers.map((t) => (
                  <td key={`${t.id}-monthly-cta`} className="py-4 px-4 text-center">
                    <TierCTA tier={t} billing="monthly" user={user} />
                  </td>
                ))}
              </tr>

              {/* Annual CTA row */}
              <tr className="bg-dark-900 border-t border-dark-600/50">
                <td className="py-4 px-4 text-sm font-semibold text-white">Annual</td>
                {tiers.map((t) => {
                  const savingsPct = calcAnnualSavingsPct(t.price_monthly_cents, t.price_annual_cents ?? 0);
                  return (
                    <td key={`${t.id}-annual-cta`} className="py-4 px-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <TierCTA tier={t} billing="annual" user={user} />
                        {t.price_monthly_cents > 0 && savingsPct > 0 && (
                          <span className="text-xs font-semibold text-green-400 bg-green-900/30 border border-green-700/40 px-2 py-0.5 rounded-full">
                            Save {savingsPct}%
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
